import { useMemo } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  ConnectionMode,
} from "@xyflow/react";
import { Box, useTheme } from "@mui/material";
import { nodeTypes } from "../../EquipmentNode";
import { edgeTypes } from "../../EquipmentEdge";
import { getAccessibleNodeColor } from "../../nodeColorPalette";
import { buildEdgesComStyle } from "../templateUtils";
import { getDiagramDotColor } from "../../styles";
import styles from "./styles";

// Corpo do preview, dentro do provider para acesso à instância do React Flow
const TemplatePreviewInterno = ({ template = null, equipmentTypes = [] }) => {
  const theme = useTheme();
  const previewStyles = styles(theme);
  // Injeta o catálogo mesclado e cores acessíveis só para renderização, igual ao canvas principal (nunca persistido)
  const nodes = useMemo(
    () =>
      (template?.dados?.nodes || []).map((node) => ({
        ...node,
        data: { ...node?.data, _equipmentTypes: equipmentTypes, _colorSwatch: getAccessibleNodeColor(node?.data?.cor, theme) },
      })),
    [template, equipmentTypes, theme],
  );
  // Estilo das edges re-derivado do lineStyle com o tema atual
  const edges = useMemo(
    () => buildEdgesComStyle(template?.dados?.edges || [], theme),
    [template, theme],
  );
  if (!template) return null;
  return (
    <Box sx={previewStyles?.previewBox}>
      <ReactFlow
        proOptions={{ hideAttribution: true }}
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        connectionMode={ConnectionMode.Loose}
        colorMode={theme?.palette?.mode}
        defaultEdgeOptions={{
          type: "equipamento",
          labelStyle: {
            fill: theme?.palette?.text?.primary,
            fontSize: 11,
            fontWeight: 600,
          },
          labelBgStyle: {
            fill: theme?.palette?.background?.paper,
            fillOpacity: 0.9,
          },
          labelBgPadding: [6, 3],
          labelBgBorderRadius: 4,
        }}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.2}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color={getDiagramDotColor(theme)}
        />
      </ReactFlow>
    </Box>
  );
};

// Preview read-only de um template, reaproveitando nó/aresta do diagrama real
export default function TemplatePreview({
  template = null,
  equipmentTypes = [],
}) {
  return (
    <ReactFlowProvider>
      <TemplatePreviewInterno
        template={template}
        equipmentTypes={equipmentTypes}
      />
    </ReactFlowProvider>
  );
}
